import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Search,
  Truck,
  Clock,
  Package,
  CheckCircle,
  MapPin,
  User,
  Calendar,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  ClipboardCheck,
  Loader2,
  Building,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { fetchEntregasClientes } from '@/services/entregasClientes.service';
import { fetchUsers } from '@/services/users.service';
import { fetchLaundries } from '@/pages/parametrizacion/laundries/Services/laundries.services.jsx';
import GestionEntregasEnCamino from '@/pages/laundry/rutas/components/GestionEntregasEnCamino';

const ReporteConductoresPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Conductores del sistema
  const [conductoresList, setConductoresList] = useState([]);
  const [loadingConductores, setLoadingConductores] = useState(false);
  const [selectedConductorId, setSelectedConductorId] = useState('');

  // Lavanderias
  const [lavanderiasList, setLavanderiasList] = useState([]);
  const [loadingLavanderias, setLoadingLavanderias] = useState(false);
  const [selectedLavanderiaId, setSelectedLavanderiaId] = useState('');

  // Filtros de fecha
  const today = new Date().toISOString().split('T')[0];
  const [fechaDesde, setFechaDesde] = useState(today);
  const [fechaHasta, setFechaHasta] = useState(today);

  // Data
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [expandedConductor, setExpandedConductor] = useState(null);
  const [expandedRecord, setExpandedRecord] = useState(null);

  // Cargar conductores al montar
  useEffect(() => {
    const loadConductores = async () => {
      setLoadingConductores(true);
      try {
        const response = await fetchUsers({
          limit: 1000,
          offset: 1,
          query: { rol: 'conductor', estado: 'Activo' },
        });
        setConductoresList(response.data || []);
      } catch (error) {
        console.error('Error al cargar conductores:', error);
        toast({ title: 'Error', description: 'No se pudieron cargar los conductores', variant: 'destructive' });
      } finally {
        setLoadingConductores(false);
      }
    };
    loadConductores();
  }, [toast]);

  // Cargar lavanderías al montar
  useEffect(() => {
    const loadLavanderias = async () => {
      setLoadingLavanderias(true);
      try {
        const response = await fetchLaundries({ limit: 500, offset: 1, query: {} });
        setLavanderiasList(response.data || response.items || []);
      } catch (error) {
        console.error('Error al cargar lavanderías:', error);
      } finally {
        setLoadingLavanderias(false);
      }
    };
    loadLavanderias();
  }, []);

  const handleBuscar = async () => {
    setIsLoading(true);
    setHasSearched(true);
    setExpandedConductor(null);
    setExpandedRecord(null);
    try {
      const desde = new Date(fechaDesde + 'T00:00:00');
      const hasta = new Date(fechaHasta + 'T23:59:59.999');

      const query = {
        createdAt: {
          $gte: desde.toISOString(),
          $lte: hasta.toISOString(),
        },
      };

      if (selectedConductorId) {
        query['conductor.id'] = selectedConductorId;
      } else {
        query['conductor.id'] = { $exists: true, $ne: null };
      }

      if (selectedLavanderiaId) {
        query['lavanderia.id'] = selectedLavanderiaId;
      }

      const response = await fetchEntregasClientes({
        query,
        limit: 1000,
        page: 1,
      });

      setRecords(response.data || []);
      if ((response.data || []).length === 0) {
        toast({ title: 'Sin resultados', description: 'No se encontraron registros para los filtros seleccionados' });
      }
    } catch (error) {
      console.error('Error al cargar registros:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar los registros', variant: 'destructive' });
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Agrupar por conductor
  const conductoresMap = {};
  records.forEach((rec) => {
    const conductorNombre = rec.conductor?.nombre || 'Sin conductor';
    const conductorId = rec.conductor?.id || 'sin-id';
    const key = `${conductorId}__${conductorNombre}`;
    if (!conductoresMap[key]) {
      conductoresMap[key] = {
        id: conductorId,
        nombre: conductorNombre,
        records: [],
      };
    }
    conductoresMap[key].records.push(rec);
  });

  const groupedConductores = Object.values(conductoresMap).sort((a, b) =>
    a.nombre.localeCompare(b.nombre)
  );

  const formatTime = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.toLocaleDateString('es-ES')} ${d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const getEstadoBadge = (estado, tipo) => {
    if (tipo === 'Recoleccion') {
      const estilos = {
        'Entregado': 'bg-red-100 text-red-800 border border-red-300',
        'Pendiente Verificacion': 'bg-green-100 text-green-800 border border-green-300',
        'Verificado': 'bg-emerald-100 text-emerald-800',
        'Cancelado': 'bg-gray-100 text-gray-800',
      };
      return estilos[estado] || 'bg-gray-100 text-gray-800';
    }
    const estilos = {
      'Alistado': 'bg-purple-100 text-purple-800',
      'En Camino': 'bg-blue-100 text-blue-800',
      'Entregado': 'bg-green-100 text-green-800',
      'Pendiente Verificacion': 'bg-amber-100 text-amber-800',
      'Verificado': 'bg-emerald-100 text-emerald-800',
      'Cancelado': 'bg-red-100 text-red-800',
    };
    return estilos[estado] || 'bg-gray-100 text-gray-800';
  };

  const getEstadoLabel = (estado, tipo) => {
    if (tipo === 'Recoleccion') {
      const labels = {
        'Entregado': '\u26a0 Pendiente entregar a lavander\u00eda',
        'Pendiente Verificacion': '\u2713 Entregado en lavander\u00eda',
        'Verificado': '\u2713 Verificado',
        'Cancelado': 'Cancelado',
      };
      return labels[estado] || estado;
    }
    return estado;
  };

  const getTipoIcon = (tipo) => {
    if (tipo === 'Recoleccion') return <Package className="h-4 w-4 text-amber-600" />;
    return <Truck className="h-4 w-4 text-blue-600" />;
  };

  const getTipoBadge = (tipo) => {
    if (tipo === 'Recoleccion') return 'bg-amber-100 text-amber-800';
    return 'bg-blue-100 text-blue-800';
  };

  // Timeline para un registro
  const TimelineItem = ({ label, time, icon: Icon, color, isLast }) => {
    const hasTime = !!time;
    return (
      <div className="flex items-start gap-3">
        <div className="flex flex-col items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${hasTime ? color : 'bg-gray-100'}`}>
            <Icon className={`h-4 w-4 ${hasTime ? 'text-white' : 'text-gray-400'}`} />
          </div>
          {!isLast && (
            <div className={`w-0.5 h-6 ${hasTime ? 'bg-gray-300' : 'bg-gray-200'}`} />
          )}
        </div>
        <div className="pt-1">
          <p className={`text-xs ${hasTime ? 'text-gray-600' : 'text-gray-400'}`}>{label}</p>
          <p className={`text-sm font-medium ${hasTime ? 'text-gray-900' : 'text-gray-400'}`}>
            {time || 'Pendiente'}
          </p>
        </div>
      </div>
    );
  };

  // Resumen de un conductor
  const getConductorResumen = (conductor) => {
    const recs = conductor.records;
    const entregas = recs.filter((r) => r.tipo === 'Entrega');
    const recolecciones = recs.filter((r) => r.tipo === 'Recoleccion');
    const entregados = recs.filter((r) => r.estado === 'Entregado' || r.estado === 'Verificado' || r.estado === 'Pendiente Verificacion');
    const totalPrendas = recs.reduce((s, r) => {
      return s + (r.items?.reduce((si, i) => si + (i.cantidad_alistada || 0), 0) || 0);
    }, 0);

    return { entregas: entregas.length, recolecciones: recolecciones.length, completadas: entregados.length, totalPrendas };
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(-1)}
                  className="h-9 w-9"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <Truck className="h-8 w-8 text-blue-600" />
                <div>
                  <CardTitle className="text-2xl">Reporte de Conductores</CardTitle>
                  <CardDescription>
                    Rutas realizadas por conductor con línea de tiempo
                  </CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filtros */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="flex items-center gap-2">
                <Label className="text-sm whitespace-nowrap">Desde:</Label>
                <Input
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  className="w-44"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm whitespace-nowrap">Hasta:</Label>
                <Input
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  className="w-44"
                />
              </div>
              <div className="flex items-center gap-2 flex-1">
                <Label className="text-sm whitespace-nowrap">Conductor:</Label>
                <select
                  value={selectedConductorId}
                  onChange={(e) => setSelectedConductorId(e.target.value)}
                  className="flex-1 h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  disabled={loadingConductores}
                >
                  <option value="">Todos los conductores</option>
                  {conductoresList.map((c) => (
                    <option key={c._id} value={c._id}>{c.nombre}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 flex-1">
                <Label className="text-sm whitespace-nowrap">Lavandería:</Label>
                <select
                  value={selectedLavanderiaId}
                  onChange={(e) => setSelectedLavanderiaId(e.target.value)}
                  className="flex-1 h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  disabled={loadingLavanderias}
                >
                  <option value="">Todas las lavanderías</option>
                  {lavanderiasList.map((l) => (
                    <option key={l._id} value={l._id}>{l.nombre}</option>
                  ))}
                </select>
              </div>
              <Button
                onClick={handleBuscar}
                disabled={isLoading}
                className="gap-2 bg-blue-600 hover:bg-blue-700"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                Buscar
              </Button>
            </div>

            {/* Resumen general */}
            {hasSearched && records.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <p className="text-xs text-blue-600">Conductores</p>
                  <p className="text-2xl font-bold text-blue-800">{groupedConductores.length}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                  <p className="text-xs text-green-600">Total registros</p>
                  <p className="text-2xl font-bold text-green-800">{records.length}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                  <p className="text-xs text-purple-600">Entregas</p>
                  <p className="text-2xl font-bold text-purple-800">{records.filter(r => r.tipo === 'Entrega').length}</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                  <p className="text-xs text-amber-600">Recolecciones</p>
                  <p className="text-2xl font-bold text-amber-800">{records.filter(r => r.tipo === 'Recoleccion').length}</p>
                </div>
              </div>
            )}

            {/* Lista de conductores */}
            {!hasSearched ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-2">Selecciona los filtros y presiona Buscar</p>
                  <p className="text-sm text-gray-400">Se mostrarán las rutas realizadas por el conductor en el rango de fechas</p>
                </CardContent>
              </Card>
            ) : isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : groupedConductores.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Truck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 mb-2">No se encontraron registros</p>
                  <p className="text-sm text-gray-400">Ajusta el rango de fechas o el conductor</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {groupedConductores.map((conductor) => {
                  const isExpanded = expandedConductor === conductor.id;
                  const resumen = getConductorResumen(conductor);

                  return (
                    <motion.div
                      key={conductor.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <Card className="border-l-4 border-l-blue-400">
                        <CardContent className="p-0">
                          {/* Header del conductor */}
                          <div
                            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50/50 transition-colors"
                            onClick={() => setExpandedConductor(isExpanded ? null : conductor.id)}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <User className="h-5 w-5 text-blue-600" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900">{conductor.nombre}</h3>
                                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <Truck className="h-3 w-3" />
                                    {resumen.entregas} entregas
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Package className="h-3 w-3" />
                                    {resumen.recolecciones} recolecciones
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <CheckCircle className="h-3 w-3" />
                                    {resumen.totalPrendas} prendas
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className="bg-blue-100 text-blue-800">
                                {conductor.records.length} registros
                              </Badge>
                              {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                            </div>
                          </div>

                          {/* Registros expandidos */}
                          {isExpanded && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              className="border-t"
                            >
                              <div className="divide-y">
                                {conductor.records
                                  .sort((a, b) => new Date(a.fecha_creacion) - new Date(b.fecha_creacion))
                                  .map((rec) => {
                                    const isRecExpanded = expandedRecord === rec._id;
                                    const totalPrendas = rec.items?.reduce((s, i) => s + (i.cantidad_alistada || 0), 0) || 0;

                                    return (
                                      <div key={rec._id}>
                                        <div
                                          className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer"
                                          onClick={() => setExpandedRecord(isRecExpanded ? null : rec._id)}
                                        >
                                          <div className="flex items-center gap-3">
                                            {getTipoIcon(rec.tipo)}
                                            <div>
                                              <div className="flex items-center gap-2">
                                                <span className="font-medium text-sm">{rec.consecutivo}</span>
                                                <Badge className={`text-xs ${getTipoBadge(rec.tipo)}`}>
                                                  {rec.tipo === 'Recoleccion' ? 'Recolección' : 'Entrega'}
                                                </Badge>
                                                <Badge className={`text-xs ${getEstadoBadge(rec.estado, rec.tipo)}`}>
                                                  {getEstadoLabel(rec.estado, rec.tipo)}
                                                </Badge>
                                              </div>
                                              <p className="text-xs text-gray-500 mt-0.5">
                                                {rec.cliente?.nombre} · {totalPrendas} prendas
                                              </p>
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs text-gray-500">
                                              {formatTime(rec.fecha_creacion)}
                                            </span>
                                            {isRecExpanded ? <ChevronUp className="h-3 w-3 text-gray-400" /> : <ChevronDown className="h-3 w-3 text-gray-400" />}
                                          </div>
                                        </div>

                                        {/* Timeline expandida */}
                                        {isRecExpanded && (
                                          <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="px-6 py-3 bg-gray-50/50 border-t border-dashed"
                                          >
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                              {/* Timeline */}
                                              <div>
                                                <p className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wider">Línea de tiempo</p>
                                                {rec.tipo === 'Entrega' ? (
                                                  <div className="space-y-0">
                                                    <TimelineItem
                                                      label="Creación"
                                                      time={formatTime(rec.fecha_creacion)}
                                                      icon={Calendar}
                                                      color="bg-gray-500"
                                                    />
                                                    <TimelineItem
                                                      label="Enviado (En camino)"
                                                      time={formatTime(rec.fecha_envio)}
                                                      icon={Truck}
                                                      color="bg-blue-500"
                                                    />
                                                    <TimelineItem
                                                      label="Entregado al cliente"
                                                      time={formatTime(rec.fecha_entrega)}
                                                      icon={CheckCircle}
                                                      color="bg-green-500"
                                                      isLast
                                                    />
                                                  </div>
                                                ) : (
                                                  <div className="space-y-0">
                                                    <TimelineItem
                                                      label="Recolección"
                                                      time={formatTime(rec.fecha_recoleccion || rec.fecha_creacion)}
                                                      icon={Package}
                                                      color="bg-amber-500"
                                                    />
                                                    <TimelineItem
                                                      label="Llegada a lavandería"
                                                      time={formatTime(rec.fecha_llegada)}
                                                      icon={MapPin}
                                                      color="bg-blue-500"
                                                    />
                                                    <TimelineItem
                                                      label="Verificación"
                                                      time={formatTime(rec.fecha_verificacion)}
                                                      icon={ClipboardCheck}
                                                      color="bg-green-500"
                                                      isLast
                                                    />
                                                  </div>
                                                )}
                                              </div>

                                              {/* Detalle */}
                                              <div>
                                                <p className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wider">Detalle</p>
                                                <div className="space-y-2 text-sm">
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-500">Cliente</span>
                                                    <span className="font-medium">{rec.cliente?.nombre}</span>
                                                  </div>
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-500">Lavandería</span>
                                                    <span className="font-medium">{rec.lavanderia?.nombre || '-'}</span>
                                                  </div>
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-500">Total prendas</span>
                                                    <span className="font-medium">{totalPrendas}</span>
                                                  </div>
                                                  {rec.totalBolsas > 0 && (
                                                    <div className="flex justify-between">
                                                      <span className="text-gray-500">Bolsas</span>
                                                      <span className="font-medium">{rec.totalBolsas}</span>
                                                    </div>
                                                  )}
                                                  {rec.recibe?.nombre && (
                                                    <div className="flex justify-between">
                                                      <span className="text-gray-500">Recibió</span>
                                                      <span className="font-medium">{rec.recibe.nombre}</span>
                                                    </div>
                                                  )}
                                                  {rec.verificado_por?.nombre && (
                                                    <div className="flex justify-between">
                                                      <span className="text-gray-500">Verificó</span>
                                                      <span className="font-medium">{rec.verificado_por.nombre}</span>
                                                    </div>
                                                  )}
                                                  {rec.observaciones && (
                                                    <div>
                                                      <span className="text-gray-500 block">Observaciones</span>
                                                      <span className="text-gray-700">{rec.observaciones}</span>
                                                    </div>
                                                  )}
                                                </div>

                                                {/* Items */}
                                                {rec.items && rec.items.length > 0 && (
                                                  <div className="mt-3">
                                                    <p className="text-xs font-semibold text-gray-500 mb-2">Prendas</p>
                                                    <div className="space-y-1">
                                                      {rec.items.map((item, idx) => (
                                                        <div key={idx} className="flex justify-between text-xs bg-white p-1.5 rounded border">
                                                          <span className="truncate">{item.nombre}</span>
                                                          <span className="font-medium ml-2">{item.cantidad_alistada}</span>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </motion.div>
                                        )}
                                      </div>
                                    );
                                  })}
                              </div>
                            </motion.div>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
        {/* Gestión de Entregas En Camino - todas las lavanderías */}
        <div className='mt-5'>
          <GestionEntregasEnCamino idLavanderia={null} />
        </div>

      </motion.div>
    </div>
  );
};

export default ReporteConductoresPage;
